import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Capture the browser's location and store lat/lng on the user's profile.
 * The server (fetchDeck / geohash trigger) does the rest. Coordinates are
 * rounded to ~1km precision — the app only needs city-level distance, and we
 * shouldn't store anything finer.
 */
export async function captureLocation(uid: string): Promise<{ lat: number; lng: number }> {
  const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Location isn't available in this browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 12_000,
      maximumAge: 10 * 60_000,
    });
  });
  const round = (n: number) => Math.round(n * 100) / 100; // ~1.1km
  const lat = round(pos.coords.latitude);
  const lng = round(pos.coords.longitude);
  await updateDoc(doc(db, "users", uid), { lat, lng });
  return { lat, lng };
}

export function geoErrorMessage(e: unknown): string {
  const code = (e as GeolocationPositionError)?.code;
  if (code === 1) return "Location permission was denied. Enable it in your browser settings to see people nearby.";
  if (code === 2) return "Couldn't determine your location. Try again.";
  if (code === 3) return "Location timed out. Try again.";
  return (e as Error)?.message ?? "Couldn't get your location.";
}
